<script>
    import { onMount } from "svelte";
    import {
        getTitle,
        goToURL,
        openViewEncounterNewPage,
        showError,
    } from "../../Utils/utilFunctions.svelte";

    import {
        CustomTooltip,
        UnsetPatientBadDebtorByMedicareModal,
    } from "./index.svelte";

    import { apiMethods } from "../../Services/index.svelte";

    import { getUnderAndOverPaymentProperties } from "../../Pages/PatientEncounter/helpers/encounterUtils.svelte";

    export let row = {},
        propertyName = "",
        openWithNewTab = false,
        clickTo = "",
        showUnderAndOverPaymentIcons = false,
        onFinish = () => {};

    $: if (showUnderAndOverPaymentIcons) _initUnderAndOverPaymentProperties();

    $: isPatientBadDebtor = propertyName
        ? row[propertyName].badDebtor
        : row.badDebtor;
    $: isPatientAlert = checkAlert(propertyName);
    $: isPatientBadDebtorByMedicare = propertyName
        ? row[propertyName].badDebtorByMedicare
        : row.badDebtorByMedicare;
    $: isPatientDeceased = propertyName
        ? row[propertyName].deceased
        : row.deceased;

    let patientName = "",
        isConfirmHideBadDebtorByMedicareModalOpen = false,
        isUnsettingPatientBadDebtorByMedicare = false;

    let showUnderPaymentIcon = false,
        underPaymentTexts = [],
        showOverPaymentIcon = false,
        overPaymentTexts = [];

    onMount(() => {
        patientName = _getPatientName();
    });

    const handleClickCell = () => {
        if (openWithNewTab) openViewEncounterNewPage(row.id);
        else goToURL(`${clickTo}/${row.id}`);
    };

    const handleClickBadDebtorByMedicare = (e) => {
        e.stopPropagation();
        _openConfirmHideBadDebtorByMedicareModal();
    };

    const checkAlert = (name) => {
        const alert = name ? row[name].alerts : row.alerts;
        if (!alert.replaceAll("<p>", "").replaceAll("</p>", "").replaceAll("<br>", "")) return false;
        else return alert;
    }

    const unsetPatientBadDebtorByMedicare = async () => {
        isUnsettingPatientBadDebtorByMedicare = true;

        const patient = propertyName ? row[propertyName] : row;

        await apiMethods.putMethod
            .updatePatient({
                ...patient,
                badDebtorByMedicare: false,
            })
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to unset patient Bad Debtor recognised by Medicare Number, please refresh the page and try again and contact support if the issue persists.",
                    );
                else {
                    _closeConfirmHideBadDebtorByMedicareModal();
                    onFinish();
                }
            });

        isUnsettingPatientBadDebtorByMedicare = false;
    };

    // HELPER FUNCTIONS
    const _getPatientName = () => {
        let data = row;

        if (propertyName) data = row[propertyName];

        const { firstName, lastName, title, middleName } = data;

        const name = `${firstName}${
            middleName ? " " + middleName : ""
        } ${lastName}`;

        return `${getTitle(title)} ${name}`;
    };

    const _openConfirmHideBadDebtorByMedicareModal = () =>
        (isConfirmHideBadDebtorByMedicareModalOpen = true);

    const _closeConfirmHideBadDebtorByMedicareModal = () =>
        (isConfirmHideBadDebtorByMedicareModalOpen = false);

    $: errorTooltipText = getTooltipText(
        isPatientBadDebtor,
        isPatientBadDebtorByMedicare,
        isPatientDeceased,
    );

    const getTooltipText = (badDebtor, badDebtorByMedicare, deceased) => {
        const text = [];

        if (deceased) text.push("Deceased");

        if (badDebtorByMedicare) text.push("Bad Debtor recognised by Medicare Number");
        else if (badDebtor) text.push("Bad Debtor");

        return text.join(", ");
    };

    const _initUnderAndOverPaymentProperties = () => {
        const res = getUnderAndOverPaymentProperties(row);

        showUnderPaymentIcon = res.showUnderPayment;
        showOverPaymentIcon = res.showOverPayment;
        underPaymentTexts = res.underPaymentTexts;
        overPaymentTexts = res.overPaymentTexts;
    };
</script>

<div
    class="flex flex-1 gap-x-2 items-center justify-start whitespace-pre-line z-100"
    on:click|preventDefault={handleClickCell}
>
    {patientName}

    {#if isPatientBadDebtor || isPatientBadDebtorByMedicare || isPatientDeceased}
        <CustomTooltip contentClass="border-rose-600">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                class="stroke-error shrink-0 w-4 h-4"
                slot="tooltipTrigger"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
            </svg>

            <p>{errorTooltipText}</p>
        </CustomTooltip>
    {/if}

    {#if isPatientAlert}
        <CustomTooltip contentClass="border-rose-600">
            <svg
                xmlns="http://www.w3.org/2000/svg"
                class="stroke-warning shrink-0 h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                slot="tooltipTrigger"
            >
                <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
            </svg>

            <p>{@html propertyName ? row[propertyName].alerts : row.alerts}</p>
        </CustomTooltip>
    {/if}

    {#if showUnderAndOverPaymentIcons}
        {#if showUnderPaymentIcon}
            <CustomTooltip>
                <i
                    class="fa-solid fa-scale-unbalanced text-pink-600 text-ms"
                    slot="tooltipTrigger"
                />

                <div class="flex flex-col gap-y-1 items-start justify-center">
                    {#each underPaymentTexts as { title, value }}
                        <p>{title}{value}</p>
                    {/each}
                </div>
            </CustomTooltip>
        {/if}

        {#if showOverPaymentIcon}
            <CustomTooltip>
                <i
                    class="fa-solid fa-scale-unbalanced-flip text-cyan-600 text-ms"
                    slot="tooltipTrigger"
                />

                <div class="flex flex-col gap-y-1 items-start justify-center">
                    {#each overPaymentTexts as { title, value }}
                        <p>{title}{value}</p>
                    {/each}
                </div>
            </CustomTooltip>
        {/if}
    {/if}
</div>

<UnsetPatientBadDebtorByMedicareModal
    bind:isModalOpen={isConfirmHideBadDebtorByMedicareModalOpen}
    closeModal={_closeConfirmHideBadDebtorByMedicareModal}
    confirmAction={unsetPatientBadDebtorByMedicare}
    isConfirming={isUnsettingPatientBadDebtorByMedicare}
/>
